import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import {
  Dialog,
  NotificationService,
  SharedModule,
} from '@vendure/admin-ui/core';

import { PricingRuleDataService } from './pricing-rule-data.service';

@Component({
  selector: 'vdr-pricing-rule-import-dialog',
  standalone: true,
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-template vdrDialogTitle>
      {{ 'marketing.import-pricing-rules' | translate }}
    </ng-template>

    <div class="form-grid">
      <div class="form-grid-span">
        <p class="description">
          {{ 'marketing.import-pricing-rules-instructions' | translate }}
        </p>
        <div class="alert alert-warning">
          <clr-icon shape="exclamation-triangle"></clr-icon>
          <span
            >Note: Coupon-only promotions (without SKUs or PDGs) are excluded
            from exports and cannot be imported. Each pricing rule must have at
            least a SKU or PDG identifier.</span
          >
        </div>
        <div class="upload-container">
          <div class="upload-area">
            <input
              #fileInput
              type="file"
              accept=".csv"
              (change)="onFileSelected($event)"
              [disabled]="loading"
              class="file-input"
            />
            <div class="upload-content">
              <clr-icon shape="upload-cloud" size="24"></clr-icon>
              <div class="upload-prompt">
                <span *ngIf="!selectedFile"
                  >Choose a CSV file or drag & drop here</span
                >
                <span *ngIf="selectedFile" class="selected-file">
                  {{ selectedFile.name }}
                  <button class="btn btn-link btn-sm" (click)="clearFile()">
                    <clr-icon shape="times"></clr-icon>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="format-section">
      <h4 class="section-title">CSV Format</h4>
      <p>{{ 'marketing.csv-format-description' | translate }}</p>
      <ul class="bullet-list">
        <li>
          <strong>ID</strong>
          <span class="optional">{{ 'common.optional' | translate }}</span>
          <span class="field-description">{{
            'marketing.id-description' | translate
          }}</span>
        </li>
        <li>
          <strong>Name</strong>
          <span class="optional">{{ 'common.optional' | translate }}</span>
          <span class="field-description">{{
            'marketing.name-description' | translate
          }}</span>
        </li>
        <li>
          <strong>PDGs</strong>
          <span class="field-description">{{
            'marketing.pdgs-description' | translate
          }}</span>
        </li>
        <li>
          <strong>SKU</strong>
          <span class="field-description">{{
            'marketing.sku-description' | translate
          }}</span>
        </li>
        <li>
          <strong>logged_in_customer_only</strong>
          <span class="field-description">{{
            'marketing.logged-in-description' | translate
          }}</span>
        </li>
        <li>
          <strong>cost_plus_percentage</strong>
          <span class="field-description">{{
            'marketing.cost-plus-description' | translate
          }}</span>
        </li>
        <li>
          <strong>list_price_discount_percentage</strong>
          <span class="field-description">{{
            'marketing.list-minus-description' | translate
          }}</span>
        </li>
      </ul>
      <div class="example-container">
        <div class="example-header">
          <span>Example:</span>
          <button class="copy-button" (click)="copyExample()">
            <clr-icon shape="copy"></clr-icon>
            Copy
          </button>
        </div>
        <pre class="csv-example">
ID,Name,PDGs,SKU,logged_in_customer_only,cost_plus_percentage,list_price_discount_percentage
,My Rule,PDG1,SKU123,Y,10,
,Another Rule,PDG2,SKU456,N,,15</pre
        >
      </div>
    </div>

    <!-- Error Display -->
    <div *ngIf="currentErrors.length > 0" class="error-section">
      <vdr-card [title]="'marketing.import-errors' | translate">
        <div class="error-table-container custom-scrollbar">
          <table class="table">
            <thead>
              <tr>
                <th>{{ 'common.line' | translate }}</th>
                <th>{{ 'common.error' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let error of currentErrors">
                <td class="line-number">{{ error.line }}</td>
                <td class="error-message">{{ error.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </vdr-card>
    </div>

    <!-- Warning Display -->
    <div *ngIf="currentWarnings.length > 0" class="warning-section">
      <vdr-card [title]="'marketing.import-warnings' | translate">
        <div class="warning-table-container custom-scrollbar">
          <table class="table">
            <thead>
              <tr>
                <th>{{ 'common.line' | translate }}</th>
                <th>Warning</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let warning of currentWarnings">
                <td class="line-number">{{ warning.line }}</td>
                <td class="warning-message">{{ warning.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </vdr-card>
    </div>

    <ng-template vdrDialogButtons>
      <button type="button" class="btn" (click)="cancel()" [disabled]="loading">
        {{ 'common.cancel' | translate }}
      </button>
      <button
        type="submit"
        (click)="startImport()"
        class="btn btn-primary"
        [disabled]="!selectedFile || loading"
      >
        <span *ngIf="!loading">{{ 'common.import' | translate }}</span>
        <span *ngIf="loading" class="btn-loading">
          <span class="spinner spinner-sm"></span>
          {{ 'common.processing' | translate }}
        </span>
      </button>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
        min-width: 500px;
        max-width: 800px;
      }

      .description {
        margin-bottom: 20px;
        color: var(--color-text-300);
        font-size: 14px;
        line-height: 1.5;
      }

      .upload-container {
        margin: 16px 0;
      }

      .upload-area {
        position: relative;
        border: 2px dashed var(--color-component-border-300);
        border-radius: 6px;
        padding: 24px;
        text-align: center;
        transition: all 0.2s;
        background: var(--color-component-bg-100);
      }

      .upload-area:hover {
        border-color: var(--color-primary-500);
        background: var(--color-component-bg-200);
      }

      .file-input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
      }

      .file-input:disabled {
        cursor: not-allowed;
      }

      .upload-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: var(--color-text-300);
      }

      .upload-prompt {
        font-size: 14px;
      }

      .selected-file {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--color-success-500);
      }

      .format-section {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid var(--color-component-border-100);
      }

      .section-title {
        margin: 0 0 16px;
        color: var(--color-text-500);
        font-size: 16px;
      }

      .bullet-list {
        list-style: none;
        margin: 16px 0;
        padding: 0;
      }

      .bullet-list li {
        position: relative;
        margin-bottom: 12px;
        padding-left: 20px;
        line-height: 1.5;
      }

      .bullet-list li::before {
        content: '•';
        position: absolute;
        left: 0;
        color: var(--color-primary-500);
        font-weight: bold;
      }

      .optional {
        display: inline-block;
        margin-left: 4px;
        font-size: 12px;
        color: var(--color-text-300);
        font-style: italic;
      }

      .field-description {
        display: block;
        margin-top: 2px;
        color: var(--color-text-300);
        font-size: 13px;
      }

      .example-container {
        margin-top: 20px;
        background: var(--color-component-bg-100);
        border: 1px solid var(--color-component-border-200);
        border-radius: 4px;
      }

      .example-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background: var(--color-component-bg-200);
        border-bottom: 1px solid var(--color-component-border-200);
        font-size: 13px;
        color: var(--color-text-300);
      }

      .copy-button {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        font-size: 12px;
        color: var(--color-text-300);
        background: none;
        border: 1px solid var(--color-component-border-300);
        border-radius: 3px;
        cursor: pointer;
        transition: all 0.2s;
      }

      .copy-button:hover {
        background: var(--color-component-bg-300);
        border-color: var(--color-component-border-400);
      }

      .csv-example {
        margin: 0;
        padding: 12px;
        font-family: monospace;
        font-size: 13px;
        line-height: 1.5;
        white-space: pre;
        overflow-x: auto;
      }

      .error-section,
      .warning-section {
        margin-top: 32px;
      }

      .error-table-container,
      .warning-table-container {
        max-height: 200px;
        overflow-y: auto;
        border: 1px solid var(--color-component-border-200);
        border-radius: 4px;
      }

      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--color-component-border-300) transparent;
      }

      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }

      .custom-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }

      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: var(--color-component-border-300);
        border-radius: 4px;
      }

      .table {
        margin: 0;
      }

      .line-number {
        width: 80px;
        text-align: center;
        color: var(--color-text-300);
        font-family: monospace;
      }

      .error-message {
        color: var(--color-error-500);
      }

      .warning-message {
        color: var(--color-warning-500);
      }

      .btn-loading {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .spinner {
        margin: 0;
      }
    `,
  ],
})
export class PricingRuleImportDialogComponent implements Dialog {
  loading = false;
  selectedFile: File | null = null;
  currentErrors: Array<{ line: number; message: string }> = [];
  currentWarnings: Array<{ line: number; message: string }> = [];
  private readonly REQUIRED_HEADERS = [
    // ID and Name are optional fields
    'PDGs',
    'SKU',
    'logged_in_customer_only',
    'cost_plus_percentage',
    'list_price_discount_percentage',
  ];

  constructor(
    private notificationService: NotificationService,
    private pricingRuleDataService: PricingRuleDataService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resolveWith() {}

  async onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      this.selectedFile = null;
      this.notificationService.error('marketing.error.no-file');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.selectedFile = null;
      this.notificationService.error('marketing.error.invalid-file-type');
      return;
    }

    // Validate CSV structure
    const validationResult = await this.validateCsvStructure(file);
    if (!validationResult.valid) {
      this.selectedFile = null;
      this.notificationService.error(
        validationResult.error === 'The CSV file is empty'
          ? 'marketing.error.empty-file'
          : 'marketing.error.missing-headers',
        validationResult.error?.includes('Missing required columns')
          ? {
              headers: validationResult.error.replace(
                'Missing required columns: ',
                '',
              ),
            }
          : undefined,
      );
      const input = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
      return;
    }

    this.selectedFile = file;
    this.changeDetector.markForCheck();
  }

  clearFile(): void {
    this.selectedFile = null;
    this.currentErrors = [];
    this.currentWarnings = [];
    const fileInput = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.changeDetector.markForCheck();
  }

  copyExample(): void {
    const example = `ID,Name,PDGs,SKU,logged_in_customer_only,cost_plus_percentage,list_price_discount_percentage
,My Rule,PDG1,SKU123,true,10,
,Another Rule,PDG2,SKU456,false,,15`;
    navigator.clipboard.writeText(example).then(() => {
      this.notificationService.success('Example CSV copied to clipboard');
    });
  }

  async startImport() {
    if (!this.selectedFile) {
      return;
    }

    this.loading = true;
    this.changeDetector.markForCheck();
    this.currentErrors = [];
    this.currentWarnings = [];
    try {
      const result = await this.importPricingRules(this.selectedFile);

      if (result.succeeded > 0) {
        this.notificationService.success('marketing.import-success', {
          count: result.succeeded,
        });
      }

      if (result.warnings && result.warnings.length > 0) {
        // Store warnings for display in the table
        this.currentWarnings = result.warnings;

        // Show an info notification with summary
        this.notificationService.info('marketing.import-with-warnings', {
          count: result.warnings.length,
        });
      }

      if (result.failed > 0) {
        // Store errors for display in the table
        this.currentErrors = result.errors;

        // Show a sticky warning notification with summary
        this.notificationService.warning('marketing.import-with-errors', {
          succeeded: result.succeeded,
          failed: result.failed,
        });
      }
    } catch (e) {
      this.notificationService.error('marketing.import-failed');
    } finally {
      this.loading = false;
      // Reset the file input if there are no errors
      if (this.currentErrors.length === 0) {
        const fileInput = document.querySelector(
          'input[type="file"]',
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
          this.selectedFile = null;
        }
      }
      this.changeDetector.markForCheck();
    }
  }

  cancel() {
    this.resolveWith();
  }

  private async validateCsvStructure(
    file: File,
  ): Promise<{ valid: boolean; error?: string }> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        const lines = content.split('\n');

        if (lines.length < 2) {
          resolve({ valid: false, error: 'The CSV file is empty' });
          return;
        }

        const headers = lines[0]
          .split(',')
          .map((h) => h.trim().replace(/^"(.*)"$/, '$1'));

        const missingHeaders = this.REQUIRED_HEADERS.filter(
          (required) => !headers.includes(required),
        );

        if (missingHeaders.length > 0) {
          resolve({
            valid: false,
            error: `Missing required columns: ${missingHeaders.join(', ')}`,
          });
          return;
        }

        resolve({ valid: true });
      };
      reader.readAsText(file);
    });
  }

  private async importPricingRules(file: File) {
    const result = await this.pricingRuleDataService.importPricingRules(file);

    if (!result) {
      throw new Error('Failed to import pricing rules');
    }

    return result;
  }
}
